// After a deploy, a tab opened before it still asks for the old build's chunks, which the server no longer has.
const RELOAD_KEY = "awraq:chunk-reload-attempted";

// One reload per minute: a chunk that still fails after the reload surfaces as an error instead of a reload loop, and
// the next deploy in the same tab reloads again.
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_LOAD_MESSAGES = [
  // Chrome
  "Failed to fetch dynamically imported module",
  // Safari
  "Importing a module script failed",
  // Firefox
  "error loading dynamically imported module",
  // Vite's preload helper, when a chunk's stylesheet is gone
  "Unable to preload CSS",
  "ChunkLoadError",
];

export const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);

  return CHUNK_LOAD_MESSAGES.some((chunkMessage) => message.includes(chunkMessage));
};

// 0, long past the cooldown, when the tab has no mark or cannot read it.
const lastReloadAt = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
};

// Without the mark a failing chunk would reload the page forever, so a tab that cannot store it never reloads.
const markReload = (): boolean => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));

    return true;
  } catch {
    // persistence-recovery: Refuse a reload without its cooldown mark, preventing an endless reload loop.
    return false;
  }
};

export const createFreshAssetsReloader = (reload: () => void = () => location.reload()) => {
  let reloading = false;

  return (error: unknown): boolean => {
    if (!isChunkLoadError(error)) {
      return false;
    }

    if (reloading) {
      return true;
    }

    if (Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS) {
      return false;
    }

    if (!markReload()) {
      return false;
    }

    reloading = true;
    reload();

    return true;
  };
};

/**
 * Reloads the page to fetch the current build when `error` is a chunk that failed to load, and returns whether the
 * page is reloading.
 */
const reloadForFreshAssets = createFreshAssetsReloader();

/**
 * Reloads the page when Vite cannot preload a chunk. Every page entry calls it, because a tab without it blanks at its
 * first lazy part after a deploy. The error still reaches the import: cancelling it here would hand the caller an
 * undefined module, which crashed Inertia and LazyMotion instead (AWRAQ-1X5, AWRAQ-2MT).
 */
export const reloadOnStaleChunks = (page: Pick<Window, "addEventListener"> = window) => {
  page.addEventListener("vite:preloadError", (event) => {
    reloadForFreshAssets(event.payload);
  });
};

/**
 * Runs a dynamic import. When its chunk is stale, the returned promise never settles, because the reload replaces the
 * page; the caller never receives a missing module and never reports the failure.
 */
export const loadChunkOrReload = <Module>(load: () => Promise<Module>): Promise<Module> =>
  load().catch((error: unknown) =>
    reloadForFreshAssets(error) ? new Promise<never>(() => {}) : Promise.reject(error),
  );
